import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Post } from '@/types'

import Link from 'next/link'

import { Seo, Layout } from '@/components/generals'

import { query } from '@/lib/db'

type BlogPost = Omit<Post, 'body' | 'published_on'> & { published_label: string }

interface BlogProps {
    posts: BlogPost[]
}

const Blog: FC<BlogProps> = ({ posts }): ReactNode => {
    const featured = posts.find((post) => post.featured) ?? posts[0]

    return (
        <Layout>
            <Seo title='آموزش' description='راهنمای کاربردی ساخت، راه‌اندازی و کار با دستگاه CNC، از انتخاب پروفیل و تنظیم کنترلر تا محاسبهٔ فید و دور.' />

            <div className='section'>
                <div className='container'>
                    <div className='blog-hero'>
                        <div className='center-title'>
                            <div className='title-holder'>
                                <div className='animate-on-load-01'>
                                    <h1 className='white-title'>آموزش‌ها</h1>
                                </div>
                                <div className='animate-on-load-02'>
                                    <div className='experience-paragraph-holder'>
                                        <p>راهنمای کاربردی ساخت، راه‌اندازی و کار با دستگاه CNC، از انتخاب پروفیل و تنظیم کنترلر تا محاسبهٔ فید و دور.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='prespective'>
                        <div className='animate-on-load-03'>
                            <div className='blog-post-main'>
                                <div className='w-dyn-list'>
                                    <div role='list' className='w-dyn-items'>
                                        {featured && (
                                            <div role='listitem' className='w-dyn-item'>
                                                <Link href={`/blog/${featured.slug}`} className='blog-post-holder w-inline-block'>
                                                    <div className='blog-post-container'>
                                                        <div className='blog-post-content'>
                                                            <div className='blog-content-holder'>
                                                                <div className='blog-category' data-mdlfy-cms={`posts:${featured.id}:category`}>{featured.category}</div>
                                                                <div className='blog-title' data-mdlfy-cms={`posts:${featured.id}:name`}>{featured.name}</div>
                                                                <p data-mdlfy-cms={`posts:${featured.id}:summary`}>{featured.summary}</p>
                                                            </div>
                                                            <p>{featured.published_label}</p>
                                                        </div>
                                                        <div className='blog-post-thumbnail-holder'><img src={featured.thumbnail_image ?? featured.main_image ?? ''} loading='lazy' alt={featured.name} className='blog-post-thumbnail' /></div>
                                                    </div>
                                                </Link>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='section'>
                <div className='container'>
                    <div className='blog-cta-holder'>
                        <div className='blog-cta-container'>
                            <div className='blog-cta-email-holder'>
                                <div className='subscribe-text'>آموزش‌های تازه را در ایمیلتان بخوانید</div>
                                <div className='from-wrapper'>
                                    <div className='form-block w-form'>
                                        <form className='subscribe-form'>
                                            <div className='from-holder'><input className='text-field-form w-input' maxLength={256} name='email-2' placeholder='ایمیل شما' aria-label='ایمیل شما' type='email' required /><input type='submit' className='button from w-button' value='عضویت' /></div>
                                        </form>
                                        <div className='success-message w-form-done'>
                                            <div>ممنون! عضویت شما ثبت شد.</div>
                                        </div>
                                        <div className='error-message w-form-fail'>
                                            <div>متأسفانه مشکلی پیش آمد. لطفاً دوباره تلاش کنید.</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='follow-us-holder'>
                                <div className='subscribe-text'>ما را دنبال کنید</div>
                                <p>آموزش‌ها و محصولات تازه را در شبکه‌های اجتماعی ببینید.</p>
                                <div className='social-media-content'>
                                    <a href='https://linkedin.com' target='_blank' rel='noreferrer' className='social-media-link w-inline-block'><img src='/images/Social-Media-Icon-White-03.svg' loading='lazy' alt='لینکدین' className='social-media-icon' /></a>
                                    <a href='https://instagram.com' target='_blank' rel='noreferrer' className='social-media-link w-inline-block'><img src='/images/Social-Media-Icon-White-02.svg' loading='lazy' alt='اینستاگرام' className='social-media-icon' /></a>
                                    <a href='https://twitter.com' target='_blank' rel='noreferrer' className='social-media-link w-inline-block'><img src='/images/Social-Media-Icon-White-01.svg' loading='lazy' alt='ایکس' className='social-media-icon' /></a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='section'>
                <div className='container'>
                    <div className='blog-grid-wrapper w-dyn-list'>
                        <div role='list' className='blog-grid w-dyn-items'>
                            {posts.map((post) => (
                                <div key={post.id} role='listitem' className='blog-grid-item w-dyn-item'>
                                    <Link href={`/blog/${post.slug}`} className='blog-item-holder w-inline-block'>
                                        <div className='blog-item-thumbnail-holder'><img src={post.thumbnail_image ?? post.main_image ?? ''} loading='lazy' alt={post.name} className='blog-item-thumbnail' /></div>
                                        <div className='blog-content'>
                                            <div className='blog-category-holder'>
                                                <div className='blog-category' data-mdlfy-cms={`posts:${post.id}:category`}>{post.category}</div>
                                                <p className='no-margins'>•</p>
                                                <p className='no-margins'>{post.published_label}</p>
                                            </div>
                                            <div className='blog-small-name' data-mdlfy-cms={`posts:${post.id}:name`}>{post.name}</div>
                                            <div className='blog-summery-holder' data-mdlfy-cms={`posts:${post.id}:summary`}>{post.summary}</div>
                                        </div>
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<BlogProps> = async () => {
    const rows = await query<Post>('SELECT id, slug, name, summary, thumbnail_image, main_image, category, featured, published_on FROM posts ORDER BY featured DESC NULLS FIRST, published_on DESC NULLS LAST')

    const posts: BlogPost[] = rows.map((post) => ({
        id: post.id,
        slug: post.slug,
        name: post.name,
        summary: post.summary,
        thumbnail_image: post.thumbnail_image,
        main_image: post.main_image,
        category: post.category,
        featured: post.featured,
        published_label: post.published_on
            ? new Date(post.published_on).toLocaleDateString('fa-IR', { month: 'long', day: 'numeric', year: 'numeric' })
            : ''
    }))

    return { props: { posts } }
}

export default Blog
